/**
 * Il canale push della telemetria.
 *
 * Stesso payload di `SensorBridge::telemetryUpdate` nell'app standalone, che a
 * sua volta e' quello del WebSocket della webapp: `{ type: "telemetry", data }`
 * con `data` mappato per cluster. Il server manda lo stato completo del
 * cluster cambiato, non la sola porzione: un client che ricevesse solo il ppm
 * e lo trattasse come lo stato perderebbe l'angolo della testa.
 *
 * La riconnessione non e' un dettaglio di robustezza. Questa pagina e'
 * l'unica cosa che dice a un operatore quanto gas c'e' adesso: se la
 * connessione cade e non torna, la pagina resta ferma su numeri vecchi e
 * sembra funzionante. Per questo `connected` e' parte dello stato pubblico —
 * chi la usa deve poter dire "questi dati non stanno arrivando".
 */

export interface ClusterState {
  ppm?: number;
  status?: "NORMAL" | "WARNING" | "CRITICAL" | "OFFLINE";
  sensorOnline?: boolean;
  ptzOnline?: boolean;
  moxaOnline?: boolean;
  updatedAt?: number;
  ptzAutoMode?: boolean;
  ptzAngle?: number;
  ptzTilt?: number;
  ptzZoom?: number;
  ptzSegIndex?: number;
  ptzDwelling?: boolean;
  ptzHoming?: boolean;
  /** Conteggio grezzo dell'encoder di tilt, quando il rig ce l'ha. Oggi la
   *  ronda pubblica solo i gradi: chi disegna il fascio sulla pianta ripiega
   *  su quelli, come fa l'app desktop sui rig senza encoder. */
  ptzEncPan?: number;
  ptzEncTilt?: number;
  /** Anemometro. Nessun impianto ne ha uno collegato — anche nell'app desktop
   *  questi campi restano vuoti — e chi li legge deve distinguere "aria
   *  calma" da "non si sta misurando". */
  windSpeed?: number;
  windDirection?: number;
  windOnline?: boolean;
}

export type TelemetrySnapshot = Record<string, ClusterState>;

export interface TelemetryHandlers {
  onUpdate: (patch: TelemetrySnapshot) => void;
  onConnectedChange?: (connected: boolean) => void;
  /** Chiuso dal server per mancata autenticazione (codice 1008). Non si
   *  ritenta: senza sessione la riconnessione e' un ciclo infinito. */
  onUnauthenticated?: () => void;
}

/** Attesa prima di riprovare, in ms. Cresce ma si ferma: martellare un server
 *  che sta riavviando lo rallenta, e aspettare mezzo minuto davanti a un
 *  impianto e' troppo. */
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000] as const;

export function connectTelemetry(
  handlers: TelemetryHandlers,
  opts: { url?: string; WebSocketImpl?: typeof WebSocket } = {},
): { close: () => void } {
  const WS = opts.WebSocketImpl ?? globalThis.WebSocket;
  const url =
    opts.url ??
    `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;

  let socket: WebSocket | undefined;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let closedByUs = false;

  const open = () => {
    if (closedByUs) return;
    socket = new WS(url);

    socket.onopen = () => {
      attempt = 0;
      handlers.onConnectedChange?.(true);
    };

    socket.onmessage = (ev: MessageEvent) => {
      // Un messaggio malformato non deve buttare giu' la pagina: si scarta e
      // si aspetta il prossimo, che arriva entro un decimo di secondo.
      try {
        const msg = JSON.parse(String(ev.data)) as { type?: string; data?: unknown };
        if (msg.type === "telemetry" && msg.data && typeof msg.data === "object") {
          handlers.onUpdate(msg.data as TelemetrySnapshot);
        }
      } catch {
        /* frame illeggibile */
      }
    };

    socket.onclose = (ev: CloseEvent) => {
      handlers.onConnectedChange?.(false);
      if (closedByUs) return;
      if (ev.code === 1008) {
        // "Authentication required": ritentare non puo' funzionare finche'
        // non si rifa il login, e un ciclo di riconnessioni respinte
        // nasconderebbe la vera causa.
        handlers.onUnauthenticated?.();
        return;
      }
      const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]!;
      attempt++;
      retryTimer = setTimeout(open, wait);
    };

    // `onerror` non porta informazioni utili nei browser: la chiusura che
    // segue e' quella che conta, e la riconnessione la gestisce onclose.
    socket.onerror = () => {};
  };

  open();

  return {
    close: () => {
      closedByUs = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
    },
  };
}

/** Fonde un aggiornamento nello stato locale. Il server manda solo i cluster
 *  cambiati, quindi sostituire l'intera mappa cancellerebbe gli altri. */
export function mergeSnapshot(
  current: TelemetrySnapshot,
  patch: TelemetrySnapshot,
): TelemetrySnapshot {
  const next = { ...current };
  for (const [id, state] of Object.entries(patch)) {
    next[id] = { ...next[id], ...state };
  }
  return next;
}
